import { FC, Suspense, useEffect, useLayoutEffect } from 'react'
import TagManager from 'react-gtm-module'
import { useLocation } from 'react-router-dom'
import { getTreatments } from '@splitsoftware/splitio-redux'
import { ITemplate } from '../interfaces/templates'
import Cart from '../templates/Cart'
import Catalog from '../templates/Catalog'
import Categories from '../templates/Categories'
import Default from '../templates/Default'
import Filters from '../templates/Filters'
import CategoriesNav from '../templates/CategoriesNav'
import { useAppDispatch, useAppSelector } from '../hooks'
import { SPLIT_NAMES } from '../consts/splitio'
import Summary from '../templates/Summary'
import ProductDetail from '../templates/ProductDetail'
import YaloAnalytics from '../modules/analytics'
import CatalogFixed from '../templates/CatalogFixed'
import OrderHistory from '../templates/OrderHistory'
import OrderDetail from '../templates/OrderDetail'
import OrderConfirmation from '../templates/OrderConfirmation'
import DeliveryDatePicker from '../templates/DeliveryDatePicker'
import PaymentMethodSelector from '../templates/PaymentMethodSelector'
import Login from '../templates/Login'
import {
  GOOGLE_TAG_MANAGER_DATA_LAYER_NAME,
  GOOGLE_TAG_LOAD_EVENT,
} from '../consts/defaultConfigValues/defaultConstants'
import { eventNames, TEventNames } from '../modules/interfaces'
import { getWebviewFeatures } from '../utils/getWebviewFeatures'

/**
 * Consumes the `default:` arm's narrowed `type` so the narrowing is checked.
 *
 * A function rather than an unused `const` so there is nothing for lint to
 * flag, and one that ignores its argument so there is nothing at runtime.
 */
const assertEveryContainerTypeHandled = (_unhandled: undefined) => undefined

const BaseContainer: FC<ITemplate> = ({ container, ...props }) => {
  const dispatch = useAppDispatch()
  const { type } = container
  const sessionData = useAppSelector((state) => state.defaultSlice.sessionData)
  const { sessionId = '', config } = useAppSelector(
    (state) => state.defaultSlice,
  )
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const [phoneNumber] = sessionData?.customer?.phoneNumber || []
  const email = sessionData?.customer?.email
  const contextAttributes = {
    email,
    phoneNumber,
  }

  dispatch(
    getTreatments({
      splitNames: SPLIT_NAMES,
      attributes: contextAttributes,
      key: storeName || '',
    }),
  )

  const getTemplate = () => {
    switch (type) {
      case 'cart':
        return <Cart container={container} {...props} />
      case 'catalog':
        return <Catalog container={container} {...props} />
      case 'filters':
        return <Filters container={container} {...props} />
      case 'categories':
        return <Categories container={container} {...props} />
      case 'summary':
        return <Summary container={container} {...props} />
      case 'categoriesnav':
        return <CategoriesNav container={container} {...props} />
      case 'productdetail':
        return <ProductDetail container={container} {...props} />
      case 'catalogFixed':
        return <CatalogFixed container={container} {...props} />
      case 'orderHistory':
        return <OrderHistory container={container} {...props} />
      case 'orderDetail':
        return <OrderDetail container={container} {...props} />
      case 'orderConfirmation':
        return <OrderConfirmation container={container} {...props} />
      case 'deliveryDatePicker':
        return <DeliveryDatePicker container={container} {...props} />
      case 'paymentMethodSelector':
        return <PaymentMethodSelector container={container} {...props} />
      case 'login':
        return <Login container={container} {...props} />
      default:
        // The compile-time link the `switch` otherwise lacks: with every
        // member of `IContainersBase['type']` cased above, `type` narrows to
        // `undefined` here — so adding a container type without adding its
        // case fails this call and names the type, instead of silently
        // rendering `Default`. A runtime `type` outside the union still lands
        // here on purpose, because it comes from remote JSON.
        assertEveryContainerTypeHandled(type)
        return <Default container={container} {...props} />
    }
  }

  const trackPageView = (
    eventName: TEventNames,
    addToEventsBatch: boolean = true,
  ) => {
    YaloAnalytics.trackPageView(
      {
        pageData: {
          isModal: false,
        },
        eventName,
        viewName: container.title,
      },
      addToEventsBatch,
    )
  }

  const location = useLocation()

  /*
   * Sticky route-change context for GA4, published in a layout effect so it
   * lands in the dataLayer **before** any descendant's passive effect fires.
   *
   * Order of operations React guarantees:
   *   1. parent + children render
   *   2. LAYOUT effects run (child-first, then parent)
   *   3. PASSIVE effects run (child-first, then parent)
   *
   * The picker templates emit `commerce-checkout_picker_open` from a passive
   * `useEffect`, so by publishing here (step 2, parent) the context is in
   * place when their event fires (step 3). An earlier revision pushed from
   * the render body, which worked but drew a side-effect-in-render warning
   * at review (correctly).
   *
   * **`location.key` is the dep that observes actual route changes.**
   * react-router issues a new `key` for every navigation entry, so two
   * same-type routes (two `catalog` routes, say) differ on `.key` even when
   * `container.type` is identical. Keying on `type` alone misses the
   * between-same-type case entirely — the previous revision had that bug.
   *
   * Fires twice under React 18 StrictMode (test environment), which is
   * harmless: the two payloads are byte-identical and the dataLayer is just
   * an append-only array. Production is still legacy `ReactDOM.render` per
   * the `index.tsx` host, so one fire per navigation holds there.
   */
  useLayoutEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        container_type: type,
        webview_features: getWebviewFeatures(config),
      },
      dataLayerName: GOOGLE_TAG_MANAGER_DATA_LAYER_NAME,
    })
    // `config` is intentionally omitted: it is a stable redux reference whose
    // identity does not move during a session, and `location.key` + the
    // container `type` already carry every change this effect observes. See
    // the sibling `useEffect` below for the same pattern on the mount push.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, type, storeName, sessionId])

  /*
   * `commerce-webview_load` is the one-per-webview session marker, distinct
   * from the sticky route-change context above. `[]` deps are correct: it
   * fires on the first mount of this BaseContainer instance and never again.
   * Because BaseContainer is reconciled rather than remounted when the
   * container `type` is the same across navigations, this effect is in
   * practice "once per webview session" for routes authored around a single
   * `type`, and "once per type" for storefronts whose routes span types.
   * Either way, it is intentionally **not** a per-route event — the sticky
   * context above is.
   */
  useEffect(() => {
    const tagManagerArgs = {
      dataLayer: {
        account_name: sessionData?.ownerInfo?.slug,
        storefront_name: storeName,
        store_id: sessionData?.customer?.code,
        container_type: type,
        webview_features: getWebviewFeatures(config),
        event: GOOGLE_TAG_LOAD_EVENT,
      },
      dataLayerName: GOOGLE_TAG_MANAGER_DATA_LAYER_NAME,
    }

    TagManager.dataLayer(tagManagerArgs)
  }, [])

  useEffect(() => {
    YaloAnalytics.trackEventsURL = config?.options?.trackEventsURL || ''
    YaloAnalytics.trackEvents = !!config?.options?.trackEventsURL || false
    window.addEventListener('beforeunload', (event) => {
      trackPageView(eventNames.webviewClosed, false)
    })
  }, [])

  useEffect(() => {
    YaloAnalytics.setBaseEventData({
      phoneNumber,
      sessionId,
      storefront: storeName || '',
      extraIds: sessionData?.extraIds || [],
    })
    trackPageView(eventNames.pageView)
  }, [
    phoneNumber,
    sessionId,
    container.title,
    storeName,
    sessionData?.extraIds,
  ])

  return <Suspense fallback={<div />}>{getTemplate()}</Suspense>
}

export default BaseContainer
